import { useState, useEffect, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { X, Search, Phone, Mail, MapPin, Users, Briefcase } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import api from '@/lib/api/axios'

interface Contact {
    id: number
    firstName: string | null
    surname: string | null
    email: string | null
    phone: string | null
    phonePrefix: string | null
    position: string | null
    team: string | null
}

interface ContactsPanelProps {
    open: boolean
    onClose: () => void
}

export function ContactsPanel({ open, onClose }: ContactsPanelProps) {
    const { t } = useTranslation()
    const [contacts, setContacts] = useState<Contact[]>([])
    const [search, setSearch] = useState('')
    const [loading, setLoading] = useState(false)
    const [total, setTotal] = useState(0)

    const fetchContacts = useCallback(async (searchTerm: string) => {
        setLoading(true)
        try {
            const params: any = { page: 1, limit: 50 }
            if (searchTerm.trim()) params.search = searchTerm.trim()
            const res = await api.get('/api/contacts', { params })
            const data = (res.data as any)?.data || res.data
            setContacts(Array.isArray(data) ? data : [])
            setTotal((res.data as any)?.total || 0)
        } catch {
            setContacts([])
        } finally {
            setLoading(false)
        }
    }, [])

    useEffect(() => {
        if (open) fetchContacts(search)
    }, [open, fetchContacts])

    useEffect(() => {
        if (!open) return
        const timer = setTimeout(() => fetchContacts(search), 300)
        return () => clearTimeout(timer)
    }, [search, open, fetchContacts])

    if (!open) return null

    return (
        <div className="fixed inset-y-0 right-0 w-96 bg-background border-l shadow-xl z-[200] flex flex-col animate-in slide-in-from-right duration-200">
            {/* Header */}
            <div className="flex items-center justify-between px-4 py-3 border-b">
                <div className="flex items-center gap-2">
                    <Users className="h-5 w-5 text-primary" />
                    <h2 className="text-base font-semibold">{t('contacts.title', 'Contacts')}</h2>
                    <span className="text-xs text-muted-foreground">({total})</span>
                </div>
                <Button variant="ghost" size="icon" className="h-7 w-7" onClick={onClose}>
                    <X className="h-4 w-4" />
                </Button>
            </div>

            {/* Search */}
            <div className="px-4 py-2 border-b">
                <div className="relative">
                    <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                    <Input
                        placeholder={t('contacts.search', 'Search contacts...')}
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        className="pl-9 h-9"
                    />
                </div>
            </div>

            {/* Contact list */}
            <div className="flex-1 overflow-y-auto">
                {loading ? (
                    <div className="flex items-center justify-center h-32 text-sm text-muted-foreground">
                        {t('common.loading', 'Loading...')}
                    </div>
                ) : contacts.length === 0 ? (
                    <div className="flex items-center justify-center h-32 text-sm text-muted-foreground">
                        {t('contacts.noContacts', 'No contacts found')}
                    </div>
                ) : (
                    <div className="divide-y">
                        {contacts.map((contact) => {
                            const fullName = [contact.firstName, contact.surname].filter(Boolean).join(' ') || '—'
                            const initials = [contact.firstName?.[0], contact.surname?.[0]].filter(Boolean).join('').toUpperCase() || '?'
                            const phoneDisplay = contact.phonePrefix
                                ? `${contact.phonePrefix} ${contact.phone || ''}`
                                : contact.phone || null

                            return (
                                <div key={contact.id} className="px-4 py-3 hover:bg-muted/50 transition-colors">
                                    <div className="flex items-start gap-3">
                                        <div className="h-9 w-9 rounded-full bg-primary/10 text-primary flex items-center justify-center text-sm font-semibold flex-shrink-0">
                                            {initials}
                                        </div>
                                        <div className="flex-1 min-w-0 space-y-1">
                                            <p className="text-sm font-medium truncate">{fullName}</p>
                                            {contact.position && (
                                                <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                                                    <Briefcase className="h-3 w-3 flex-shrink-0" />
                                                    <span className="truncate">{contact.position}</span>
                                                </div>
                                            )}
                                            {contact.team && (
                                                <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                                                    <Users className="h-3 w-3 flex-shrink-0" />
                                                    <span className="truncate">{contact.team}</span>
                                                </div>
                                            )}
                                            {phoneDisplay && (
                                                <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                                                    <Phone className="h-3 w-3 flex-shrink-0" />
                                                    <a href={`tel:${phoneDisplay.replace(/\s/g, '')}`} className="hover:text-primary truncate">
                                                        {phoneDisplay}
                                                    </a>
                                                </div>
                                            )}
                                            {contact.email && (
                                                <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                                                    <Mail className="h-3 w-3 flex-shrink-0" />
                                                    <a href={`mailto:${contact.email}`} className="hover:text-primary truncate">
                                                        {contact.email}
                                                    </a>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            )
                        })}
                    </div>
                )}
            </div>
        </div>
    )
}
